import React, { useCallback, useEffect, useRef, useState } from 'react';
import TextInput from '@common/components/Inputs/TextInput';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import {
  CrossCloseIcon,
  LocationsIcon,
  OrganizationsIcon,
  RobotIcon,
  SearchIcon,
  UserIcon,
} from '@common/utils/icons/icons';
import { useAppDispatch, useAppSelector } from '../../store';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { selectSearchData } from '../../store/organization/organization-slice-selectors';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { getSearchResults } from '../../store/organization/organization-actions';
import SearchType from '../../models/enums/search';
import { getCurrentPageName, trackEvent } from '../../services/analytics';
import { AnalyticsEvent } from '../../services/analytics-events';
import { resetSearchRestStatus } from '../../store/organization/organization-slice';
import SearchResult from '../../models/misc/search-result';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FilterKeys, navigateSphereWithFilters } from '@common/enums/query-params/filters';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import Popper from '@mui/material/Popper';
import { replaceArgsInMessage } from '../../utils/strings';
import { setSelectedMissionId } from '../../store/robots/robots-slice';
import OrganizationRole from '../../models/enums/organization-roles';

export const searchHistoryStorageName = 'sphere-search-history';

const SearchBar: React.FC<{
  isSmallScreenSearch?: boolean;
  suggestions?: SearchResult[];
  topLabel?: string;
  initialValue?: string;
  fields?: SearchType[];
  fullSize?: boolean;
  inline?: boolean;
  placeholder?: string;
  childOrgId?: string;
  orgRole?: OrganizationRole;
  hideTitle?: boolean;
  includeHistory?: boolean;
  resultsFilter?: (result: SearchResult) => boolean;
  onClose?: () => void;
  onItemClick?: (result: SearchResult) => void;
}> = ({
  isSmallScreenSearch,
  suggestions,
  topLabel,
  initialValue,
  fields,
  fullSize,
  inline,
  placeholder,
  hideTitle,
  childOrgId,
  orgRole,
  includeHistory,
  resultsFilter,
  onClose,
  onItemClick,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [_, setFilterParams] = useSearchParams();

  const searchData = useAppSelector((state) => selectSearchData(state));

  const [searchValue, setSearchValue] = useState(initialValue ?? '');
  const [searchResultsAnchorEl, setSearchResultsAnchorEl] = React.useState<HTMLDivElement | null>(
    null,
  );
  const [history, setHistory] = useState<SearchResult[]>([]);

  const popperRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement | null>(null);
  // Set when a result is picked, so closing the search afterwards is not counted as abandoned.
  const resultClickedRef = useRef(false);

  useEffect(() => {
    if (initialValue != null) {
      setSearchValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (includeHistory) {
      const storedHistory = localStorage.getItem(searchHistoryStorageName);
      if (storedHistory) {
        setHistory(JSON.parse(storedHistory) as SearchResult[]);
      }
    }
  }, [includeHistory]);

  const searchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    if (event.target.value.length > 5) {
      getSearchResultsHandler(event.target.value);
    }
  };

  const closeSearchHandler = useCallback(() => {
    if (!resultClickedRef.current && searchValue.length > 0) {
      trackEvent(AnalyticsEvent.SearchAbandoned, {
        query_length: searchValue.length,
        result_count: searchData.data.length,
        search_status: HttpFetchStatus[searchData.getStatus],
        source_page: getCurrentPageName(),
      });
    }
    resultClickedRef.current = false;
    if (onClose != null) {
      onClose();
    }
    dispatch(resetSearchRestStatus());
    setSearchValue('');
    setSearchResultsAnchorEl(null);
  }, [dispatch, onClose, searchValue.length, searchData.data.length, searchData.getStatus]);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (
        searchBarRef.current &&
        popperRef.current &&
        !searchBarRef.current.contains(target) &&
        !popperRef.current.contains(target)
      ) {
        closeSearchHandler();
      }
    },
    [closeSearchHandler],
  );

  useEffect(() => {
    if (inline !== true) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handleClickOutside, inline]);

  const searchSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    trackEvent(AnalyticsEvent.SearchSubmitted, {
      query_length: searchValue.length,
      search_fields: fields ?? [],
      source_page: getCurrentPageName(),
    });
    getSearchResultsHandler(searchValue);
  };

  const getSearchResultsHandler = (searchValue: string) => {
    dispatch(getSearchResults({ search: searchValue, fields: fields ?? [], childOrgId, orgRole }));
  };

  const resultClickHandler = (result: SearchResult) => {
    resultClickedRef.current = true;
    trackEvent(AnalyticsEvent.SearchResultClicked, {
      result_type: result.type,
      result_source: suggestions?.some((s) => s.id === result.id)
        ? 'suggestion'
        : searchData.data.some((r) => r.id === result.id)
          ? 'search'
          : 'history',
      result_position: searchResults.findIndex((r) => r.id === result.id),
      result_count: searchResults.length,
      query_length: searchValue.length,
      is_embedded: onItemClick != null,
      source_page: getCurrentPageName(),
    });
    if (onItemClick != null) {
      resultClickedRef.current = false;
      onItemClick(result);
      setSearchResultsAnchorEl(null);
      dispatch(resetSearchRestStatus());
      return;
    }

    switch (result.type) {
      case SearchType.ORGANIZATION:
        navigate(navigateSphereWithFilters(result.id, true));
        break;
      case SearchType.ROBOT:
        dispatch(setSelectedMissionId(null));
        setFilterParams((prevState) => {
          prevState.delete(FilterKeys.MissionId);
          prevState.delete(FilterKeys.MissionHistoryId);
          prevState.delete(FilterKeys.UserId);
          prevState.set(FilterKeys.RobotId, result.id);
          return prevState;
        });
        break;
      case SearchType.USER:
        dispatch(setSelectedMissionId(null));
        setFilterParams((prevState) => {
          prevState.delete(FilterKeys.MissionId);
          prevState.delete(FilterKeys.MissionHistoryId);
          prevState.delete(FilterKeys.RobotId);
          prevState.set(FilterKeys.UserId, result.id);
          return prevState;
        });
        break;
      default:
        break;
    }
    closeSearchHandler();
    if (includeHistory) {
      setHistory((prevState) => {
        const newHistory = [result, ...prevState.filter((h) => h.id !== result.id).slice(0, 4)]; // FIFO buffer length 5
        localStorage.setItem(searchHistoryStorageName, JSON.stringify(newHistory));
        return newHistory;
      });
    }
  };

  const openSearchResultsPopoverHandler = () => {
    setSearchResultsAnchorEl(searchBarRef.current);
  };

  const searchResults = (
    suggestions?.filter((result) =>
      result.name.toLowerCase().includes(searchValue.toLowerCase()),
    ) ?? []
  )
    .concat(
      searchData.data.filter(
        (result) => !suggestions?.some((suggestion) => suggestion.id === result.id),
      ),
    )
    .concat(includeHistory && searchData.data.length === 0 ? history : []);

  const getResultSubtitle = (result: SearchResult) => {
    if (!result.additional) return null;

    let parts: (string | null)[] = [];
    switch (result.type) {
      case SearchType.ROBOT:
        parts = [
          result.additional.model ? t('robot-model-' + result.additional.model) : null,
          result.additional.serialNumber ?? null,
        ];
        break;
      case SearchType.ORGANIZATION:
        parts = [
          result.additional.role ? t('organization-role-' + result.additional.role) : null,
          result.additional.parentName ??
            (result.additional.country ? t('country-' + result.additional.country) : null),
        ];
        break;
      case SearchType.USER:
        parts = [
          result.additional.role ? t('user-role-' + result.additional.role) : null,
          result.additional.organizationName ?? null,
        ];
        break;
      default:
        break;
    }

    const filteredParts = parts.filter((p) => p !== null);
    return filteredParts.length > 0 ? filteredParts.join(' · ') : null;
  };

  const content = (
    <Stack
      spacing={1}
      sx={(theme) => ({
        zIndex: theme.zIndex.modal,
        backgroundColor: theme.palette.background.paper,
        padding: 2,
        boxShadow: !inline ? 2 : 0,
        borderRadius: 1,
        maxHeight: theme.spacing(48),
        width: '100%',
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {hideTitle !== true && <Typography variant="h2">{t('title-search-results')}</Typography>}
        {!isSmallScreenSearch && !inline && (
          <IconButton onClick={closeSearchHandler} size="small">
            <CrossCloseIcon />
          </IconButton>
        )}
      </Stack>
      <CustomAlert
        icon={
          searchData.getStatus === HttpFetchStatus.Loading ? (
            <CircularProgress size={24} />
          ) : undefined
        }
        header={
          <Stack direction="row" alignItems="center" spacing={1}>
            {searchData.getStatus === HttpFetchStatus.Loading
              ? t('loading-search-results')
              : searchData.data.length === 0 && searchData.getStatus === HttpFetchStatus.Success
                ? t('info-nothing-found')
                : searchData.count > 40
                  ? replaceArgsInMessage(t('info-refine-search'), [
                      searchData.data.length,
                      searchData.count,
                    ])
                  : searchData.getStatus === HttpFetchStatus.Success
                    ? t('info-search-results')
                    : t('info-start-search')}
          </Stack>
        }
        severity="info"
      />
      <Stack height="100%" overflow="auto">
        {includeHistory && searchData.data.length === 0 && history.length > 0 && (
          <Typography mt={1} variant="subtitle2">
            {t('title-previous-searches')}
          </Typography>
        )}
        {searchResults.filter(resultsFilter ?? ((_) => true)).map((result) => {
          const subtitle = getResultSubtitle(result);
          return (
            <Button
              sx={(theme) => ({
                px: 2,
                justifyContent: 'flex-start',
                color: theme.palette.action.active,
              })}
              onClick={() => resultClickHandler(result)}
              key={result.id}
              size="large"
              startIcon={
                result.type === SearchType.ORGANIZATION ? (
                  result.role === OrganizationRole.LOCATION ? (
                    <LocationsIcon sx={{ mr: 2 }} />
                  ) : (
                    <OrganizationsIcon sx={{ mr: 2 }} />
                  )
                ) : result.type === SearchType.ROBOT ? (
                  <RobotIcon sx={{ mr: 2 }} />
                ) : result.type === SearchType.USER ? (
                  <UserIcon sx={{ mr: 2 }} />
                ) : (
                  <SearchIcon sx={{ mr: 2 }} />
                )
              }
            >
              {subtitle ? (
                <Stack alignItems="flex-start" width="100%" textAlign="left">
                  {result.name}
                  <Typography variant="caption" color="text.secondary">
                    {subtitle}
                  </Typography>
                </Stack>
              ) : (
                result.name
              )}
            </Button>
          );
        })}
      </Stack>
    </Stack>
  );

  return (
    <React.Fragment>
      <Stack
        ref={searchBarRef}
        direction="row"
        alignItems="center"
        sx={(theme) => ({
          height: fullSize ? undefined : theme.spacing(6),
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.spacing(3),
          overflow: topLabel ? undefined : 'hidden',
          width: isSmallScreenSearch || fullSize ? `calc(100% - ${theme.spacing(2)})` : '50%',
          maxWidth: fullSize ? '100%' : theme.spacing(96),
          mx: isSmallScreenSearch ? 1 : undefined,
        })}
      >
        <form onSubmit={searchSubmitHandler} style={{ width: '100%' }}>
          <TextInput
            label={topLabel}
            onFocus={openSearchResultsPopoverHandler}
            onChange={searchChangeHandler}
            value={searchValue}
            fullWidth={isSmallScreenSearch}
            autoComplete="off" // Disables browser autocomplete suggestions
            placeholder={placeholder ?? t('input-label-search')}
            variant="outlined"
            endAdornment={
              <IconButton type="submit">
                <SearchIcon />
              </IconButton>
            }
          />
        </form>
        {isSmallScreenSearch && (
          <IconButton onClick={closeSearchHandler}>
            <CrossCloseIcon />
          </IconButton>
        )}
      </Stack>

      {inline ? (
        content
      ) : (
        <Popper
          id="search-results-popover-id"
          open={Boolean(searchResultsAnchorEl)}
          anchorEl={searchResultsAnchorEl}
          placement="bottom"
          ref={popperRef}
          sx={(theme) => ({
            zIndex: theme.zIndex.modal,
            width: searchResultsAnchorEl?.clientWidth,
            pt: 1,
          })}
        >
          {content}
        </Popper>
      )}
    </React.Fragment>
  );
};

export default SearchBar;
